import React, { useState, useEffect, useCallback } from 'react';
import LiveDispatch from './LiveDispatch/LiveDispatch';
import ItineraryDispatch from './Itinerary/ItineraryDispatch';
import DemandResponse from './DemandResponse/DemandResponse';
import ShuttlebusRouter from './ShuttlebusRouter';
import Nightowl from './Nightowl/Nightowl';
import RouteSelection from './RouteSelection';
import BeginShift from './BeginShift';
import CompleteShift from './CompleteShift';
import comms from '../comms';

function Dispatch({ navigateTo, userSettings, fleetSettings, socket, routeDetails, driverAuthData, selectedVehicle: propSelectedVehicle, onDispatchDataUpdate, googleMapsLoaded, onDispatchTypeChange }) {
  // Use selectedVehicle from driverAuthData first, then fall back to the prop from App
  const selectedVehicle = driverAuthData?.dispatchData?.vehicleObject || propSelectedVehicle;
  const [currentDriverAuthData, setCurrentDriverAuthData] = useState(driverAuthData);
  const [isOpeningShift, setIsOpeningShift] = useState(false);

  // Fallback: if userSettings prop is null/empty, read from localStorage.
  // This covers the window between auth (comms.js stores it) and App.js state sync.
  // Use a local ref to avoid re-renders from prop reassignment (which was broken before).
  const effectiveUserSettings = React.useMemo(() => {
    if (userSettings && Object.keys(userSettings).length > 0) {
      return userSettings;
    }
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('userSettings');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
            console.log('Dispatch: Using userSettings from localStorage fallback');
            return parsed;
          }
        }
      } catch (e) {
        console.error('Dispatch: Error reading userSettings from localStorage:', e);
      }
    }
    return userSettings;
  }, [userSettings]);

  // Update local state when props change
  useEffect(() => {
    if (driverAuthData && JSON.stringify(driverAuthData) !== JSON.stringify(currentDriverAuthData)) {
      console.log('Dispatch: Updating current driver auth data from props:', driverAuthData);
      setCurrentDriverAuthData(driverAuthData);
    }
  }, [driverAuthData, currentDriverAuthData]);

  // We'll handle shift opening in the render logic instead of useEffect

  const determineDispatchType = useCallback(() => {
    // Get route details from driverAuthData, not from localStorage
    const routeObject = currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject;
    
    if (!routeObject || typeof routeObject !== 'object') return "live";

    const itinerary = Array.isArray(routeObject.itinerary) ? routeObject.itinerary : [];
    const trips = Array.isArray(routeObject.trips) ? routeObject.trips : [];
    const routeType = typeof routeObject.routeType === 'string' ? routeObject.routeType.toLowerCase() : undefined;
    const serviceType = typeof routeObject.servicetype === 'string' ? routeObject.servicetype.toLowerCase() : undefined;
    const routeName = typeof routeObject.Name === 'string' ? routeObject.Name.toLowerCase() : '';

    // Check for nightowl routes first
    if (routeType === "nightowl" || 
        serviceType === "nightowl" ||
        routeName.includes('nightowl') ||
        routeName.includes('night owl')) {
      return "nightowl";
    }

    // Check for demand response routes
    if (routeType === "demandresponse" || 
        routeType === "demand-response" ||
        serviceType === "demandresponse" ||
        serviceType === "demand-response" ||
        routeName.includes('demand response') ||
        routeName.includes('demandresponse')) {
      return "demandresponse";
    }

    // Handle fixed routes (both looped and fixed schedules)
    if (routeType === "fixedroute") {
//last object in itinerary
      const lastItineraryItem = itinerary[itinerary.length - 1];
      const lastItineraryItemIsEndStep = lastItineraryItem?.endStep || false;
      const lastItineraryItemIsArrived = lastItineraryItem?.isArrived || lastItineraryItem?.arrivedAt || lastItineraryItem?.status === 'arrived' || false;
      
      if (itinerary.length > 0 && !(lastItineraryItemIsEndStep && lastItineraryItemIsArrived)) {
        // Check if all itinerary items have the same RunId (indicates looped)
        const runIds = [...new Set(itinerary.map(item => item.RunId))];
        return runIds.length === 1 ? "shuttlebus-looped" : "shuttlebus-fixed";
      } else {
        // Empty itinerary indicates route completed but shift not - direct to completeShift
        return "completeShift";
      }
    }

    const hasStructuredRouteData = itinerary.length > 0 || trips.length > 0;

    // Route/RouteMaster based routes with structured itinerary should use itinerary component
    if (routeType === "route" || routeType === "itinerary" || hasStructuredRouteData) {
      if (hasStructuredRouteData) {
        return "itinerary";
      }
      return "live";
    }
    
    // Default to live dispatch
    return "live";
    //return "itinerary";
  }, [currentDriverAuthData]);

  // Pure route-type classification for the Sidebar/Dashboard "Chat with
  // Dispatch" / Messages gate — mirrors determineDispatchType's structural
  // checks but never collapses to "completeShift". determineDispatchType
  // returns "completeShift" once a fixed route's itinerary is finished so the
  // *stage machine* renders the Complete Shift screen (correct, unchanged
  // below) — but that value was also being reported as the route's "type" to
  // the parent, which made Chat with Dispatch / Messages think a finished
  // itinerary/shuttle route was no longer an itinerary/shuttle route at all,
  // hiding chat at exactly the moment a driver finishing a route might most
  // want to message dispatch.
  const getRouteDispatchType = useCallback(() => {
    const routeObject = currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject;
    if (!routeObject || typeof routeObject !== 'object') return "live";

    const itinerary = Array.isArray(routeObject.itinerary) ? routeObject.itinerary : [];
    const trips = Array.isArray(routeObject.trips) ? routeObject.trips : [];
    const routeType = typeof routeObject.routeType === 'string' ? routeObject.routeType.toLowerCase() : undefined;
    const serviceType = typeof routeObject.servicetype === 'string' ? routeObject.servicetype.toLowerCase() : undefined;
    const routeName = typeof routeObject.Name === 'string' ? routeObject.Name.toLowerCase() : '';

    if (routeType === "nightowl" ||
        serviceType === "nightowl" ||
        routeName.includes('nightowl') ||
        routeName.includes('night owl')) {
      return "nightowl";
    }

    if (routeType === "demandresponse" ||
        routeType === "demand-response" ||
        serviceType === "demandresponse" ||
        serviceType === "demand-response" ||
        routeName.includes('demand response') ||
        routeName.includes('demandresponse')) {
      return "demandresponse";
    }

    if (routeType === "fixedroute") {
      const runIds = [...new Set(itinerary.map(item => item.RunId))];
      return runIds.length === 1 ? "shuttlebus-looped" : "shuttlebus-fixed";
    }

    const hasStructuredRouteData = itinerary.length > 0 || trips.length > 0;
    if (routeType === "route" || routeType === "itinerary" || hasStructuredRouteData) {
      return hasStructuredRouteData ? "itinerary" : "live";
    }

    return "live";
  }, [currentDriverAuthData]);

  // Determine where in the dispatch login process we are
  const determineDispatchStage = useCallback(() => {
    const routeRequired = currentDriverAuthData?.routeRequired;
    const shiftRequired = currentDriverAuthData?.shiftRequired;
    const inspectionRequired = currentDriverAuthData?.inspectionRequired;
    const shiftObject = currentDriverAuthData?.shiftObject;
    const shiftId = currentDriverAuthData?.shiftId;
    
    // Get route data from driverAuthData, not from localStorage
    const routeObject = currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject;

    console.log('Dispatch: Determining stage with data:', {
      routeRequired,
      shiftRequired,
      inspectionRequired,
      beginningOdometerRequired: currentDriverAuthData?.beginningOdometerRequired,
      odometerRequired: currentDriverAuthData?.odometerRequired,
      hasShiftObject: !!shiftObject,
      hasShiftId: !!shiftId,
      hasRouteObject: !!routeObject,
      isOpeningShift,
      routeObjectKeys: routeObject ? Object.keys(routeObject).length : 0
    });

    // Check if route is required and if we need to go to route selection
    if (routeRequired) {
      // Route is required - check if we have a properly selected route
      const hasValidRoute = routeObject && 
                           typeof routeObject === 'object' && 
                           Object.keys(routeObject).length > 0;

      if (!hasValidRoute) {
        console.log('Dispatch: Route required but not valid, going to routeSelection');
        return "routeSelection";
      }

      // Check if the route object indicates it's not actually selected
      // Look for indicators that this is just a placeholder/default route
      const routeId = routeObject.routeId;
      const routeName = routeObject.routeName;
      
      // If route name contains the ID or looks like a placeholder, it's probably not selected
      const isPlaceholderRoute = routeName && routeId && routeName.includes(routeId);
      
      // If routeId is null or undefined, route is not selected
      if (!routeId || isPlaceholderRoute) {
        console.log('Dispatch: Route appears to be placeholder, going to routeSelection');
        return "routeSelection";
      }
    }

    // Only open shift if shift is required AND we don't have a shift object or shift ID
    // If we have either a shiftObject or shiftId, the shift is already created during login
    // This prevents redundant shift creation when the user already has an active shift
    if (shiftRequired && !shiftObject && !shiftId && !isOpeningShift) {
      return "openShift";
    }

    // If inspection or odometer is required, go to beginShift
    const odometerRequired = currentDriverAuthData?.beginningOdometerRequired || currentDriverAuthData?.odometerRequired;
    if (inspectionRequired || odometerRequired) {
      console.log('Dispatch: Going to beginShift - inspection required:', inspectionRequired, 'odometer required:', odometerRequired);
      return "beginShift";
    }

    // If we have route object, route to appropriate dispatch type
    if (routeObject) {
      return determineDispatchType();
    }

    // Default to live dispatch for non-route-required workflows
    return "live";
  }, [currentDriverAuthData, isOpeningShift, determineDispatchType]);

  // Handle shift opening via socket
  const handleOpenShift = useCallback(async () => {
    if (!socket || isOpeningShift) return;

    setIsOpeningShift(true);
    
    try {
      
      socket.emit('openShift', {
        driverCode: currentDriverAuthData?.driverCode || currentDriverAuthData?.Code || currentDriverAuthData?.code || userSettings?.Code || userSettings?.code,
        vehicleId: selectedVehicle?.Id || selectedVehicle?.id,
        driverId: userSettings?.Id || userSettings?.id
      }, (response) => {
        console.log('Dispatch: Open shift response:', response);
        setIsOpeningShift(false);
        
        if (response?.success) {
          
          // Normalize the shift data from socket - server returns raw SQL record with PascalCase Id
          // but dispatchData.shiftObject from REST auth uses camelCase id (from loadShiftData)
          const socketShiftData = response.dispatchData?.shiftData;
          const normalizedShiftData = socketShiftData ? {
            ...socketShiftData,
            id: socketShiftData.Id || socketShiftData.id,
            Id: socketShiftData.Id || socketShiftData.id,
          } : null;
          
          // Update driver auth data with new shift information
          // CRITICAL: Also update dispatchData.shiftObject so CompleteShift.js can find it
          const updatedDispatchData = currentDriverAuthData?.dispatchData 
            ? { ...currentDriverAuthData.dispatchData, shiftObject: normalizedShiftData || {} }
            : { shiftObject: normalizedShiftData || {} };
          
          const updatedAuthData = {
            ...currentDriverAuthData,
            shiftId: socketShiftData?.Id || socketShiftData?.id,
            shiftObject: normalizedShiftData, // Top-level for fallback access
            dispatchData: updatedDispatchData, // Update nested dispatchData.shiftObject too
            shiftRequired: false, // Shift is now open
            // Incorporate route, driver, and vehicle data from openShift response
            // These are additive — only set if response provides them, otherwise keep existing
            routeObject: response.dispatchData?.routeObject || currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject,
            driverObject: response.dispatchData?.driverData || currentDriverAuthData?.driverObject,
            vehicleObject: response.dispatchData?.vehicleData || currentDriverAuthData?.vehicleObject || currentDriverAuthData?.dispatchData?.vehicleObject,
          };
          
          setCurrentDriverAuthData(updatedAuthData);

          // Multi-shift day (route self-scheduling §4.9): map the just-opened
          // shift to the claimed route for "now" so each claimed route drives
          // its own shift. Best-effort — failures must never block a single-
          // route fleet from starting its shift.
          const openedShiftId = socketShiftData?.Id || socketShiftData?.id;
          const openedDriverId = userSettings?.Id || userSettings?.id;
          if (openedShiftId && openedDriverId) {
            comms
              .getShiftAction({ driverId: openedDriverId, vehicleId: selectedVehicle?.Id || selectedVehicle?.id })
              .then((action) => {
                const routeId =
                  action?.routeId ||
                  response.dispatchData?.routeObject?.routeId ||
                  response.dispatchData?.routeObject?.RouteId;
                if (action?.available === false || !routeId) return;
                return comms.mapRouteShift({ routeId, shiftId: openedShiftId, driverId: openedDriverId });
              })
              .catch((err) => console.warn('Multi-shift map skipped:', err?.message));
          }

          // Use setTimeout to ensure state update is complete before callback
          setTimeout(() => {
            // Notify parent component of the updated dispatch data
            if (onDispatchDataUpdate) {
              onDispatchDataUpdate({
                driverAuthData: updatedAuthData,
                shiftData: response.dispatchData?.shiftData,
                routeDetails: response.dispatchData?.routeObject
              });
            }
          }, 0);
        } else {
          console.error('Failed to open shift:', response?.error);
          // Handle error - maybe show notification or navigate back
        }
      });
    } catch (error) {
      console.error('Error opening shift:', error);
      setIsOpeningShift(false);
    }
  }, [socket, isOpeningShift, selectedVehicle?.Id, selectedVehicle?.id, currentDriverAuthData, onDispatchDataUpdate, userSettings?.Id, userSettings?.id, userSettings?.Code, userSettings?.code]);

  const currentStage = determineDispatchStage();

  // Notify parent of dispatch type changes for sidebar menu. Uses
  // getRouteDispatchType (not determineDispatchType) so the reported type
  // stays "itinerary"/"shuttlebus-*" even once the route/itinerary is
  // finished and the stage machine has moved on to "completeShift" below.
  useEffect(() => {
    if (onDispatchTypeChange && typeof onDispatchTypeChange === 'function') {
      onDispatchTypeChange(getRouteDispatchType());
    }
  }, [currentDriverAuthData, getRouteDispatchType, onDispatchTypeChange]);

  // Handle route selection completion
  const handleRouteSelected = useCallback((newRouteDetails) => {
    console.log('Dispatch: Route selected:', newRouteDetails);
    
    // Update driver auth data with the selected route
    const updatedAuthData = {
      ...currentDriverAuthData,
      routeObject: newRouteDetails,
      routeRequired: false // Route is now selected
    };
    
    setCurrentDriverAuthData(updatedAuthData);
    
    // Use setTimeout to ensure state update is complete before callback
    setTimeout(() => {
      // Update parent component with new route details and updated auth data
      if (onDispatchDataUpdate) {
        onDispatchDataUpdate({
          driverAuthData: updatedAuthData,
          routeDetails: newRouteDetails
        });
      }
    }, 0);
  }, [currentDriverAuthData, onDispatchDataUpdate]);

  // Handle begin shift completion - uses completion flag pattern instead of callbacks
  const handleBeginShiftComplete = useCallback((completionData) => {
    console.log('Dispatch: Begin shift complete:', completionData);
    
    // completionData can be a boolean (legacy) or an object with flags
    const isLegacyCall = typeof completionData === 'boolean';
    
    // Update driver auth data to mark inspection and odometer as complete
    const updatedAuthData = {
      ...currentDriverAuthData,
      inspectionRequired: false,
      inspectionCompleted: true,
      beginningOdometerRequired: false,
      odometerRequired: false,
      odometerCompleted: true
    };
    
    setCurrentDriverAuthData(updatedAuthData);
    
    // Notify parent of data update
    if (onDispatchDataUpdate) {
      onDispatchDataUpdate({
        driverAuthData: updatedAuthData,
        routeDetails: currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject,
        inspectionData: isLegacyCall ? completionData : completionData
      });
    }
  }, [currentDriverAuthData, onDispatchDataUpdate]);

  // Use effect to detect inspection completion and trigger stage re-evaluation
  useEffect(() => {
    if (currentDriverAuthData?.inspectionCompleted && currentDriverAuthData?.odometerCompleted) {
      console.log('Dispatch: Inspection and odometer marked complete, re-evaluating stage...');
      // Stage will be re-evaluated automatically via determineDispatchStage dependency on currentDriverAuthData
    }
  }, [currentDriverAuthData?.inspectionCompleted, currentDriverAuthData?.odometerCompleted]);

  const renderDispatchStage = () => {
    switch (currentStage) {
      case 'routeSelection':
        return (
          <RouteSelection
            selectedVehicle={selectedVehicle}
            fleetSettings={fleetSettings}
            onRouteSelected={handleRouteSelected}
            onBack={() => navigateTo('login')}
          />
        );

      case 'openShift':
        // Auto-trigger shift opening when this case is rendered
        if (!isOpeningShift) {
          handleOpenShift();
        }
        
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'var(--app-content-height)' }}>
            <div>
              <p>Opening shift...</p>
              {/* You could add a spinner here */}
            </div>
          </div>
        );

      case 'beginShift':
        const shiftObjectId = currentDriverAuthData?.shiftObject?.Id || 
                             currentDriverAuthData?.shiftObject?.id || 
                             currentDriverAuthData?.shiftObject?.shiftId ||
                             currentDriverAuthData?.shiftObject?.ShiftId;
        const finalShiftId = currentDriverAuthData?.shiftId || shiftObjectId;
        
        return (
          <BeginShift
            selectedVehicle={selectedVehicle}
            fleetSettings={fleetSettings}
            socket={socket}
            onBeginShiftComplete={handleBeginShiftComplete}
            onBack={() => navigateTo('login')}
            shiftId={finalShiftId}
            shiftObject={currentDriverAuthData?.shiftObject}
            currentOdometerReading={currentDriverAuthData?.currentOdometerReading}
            odometerRequired={currentDriverAuthData?.beginningOdometerRequired || currentDriverAuthData?.odometerRequired || false}
            inspectionData={currentDriverAuthData?.inspectionObject || currentDriverAuthData?.inspectionData}
          />
        );

      case 'shuttlebus-looped':
      case 'shuttlebus-fixed':
        return (
          <ShuttlebusRouter
            socket={socket}
            navigateTo={navigateTo}
            userSettings={effectiveUserSettings}
            fleetSettings={fleetSettings}
            selectedVehicle={selectedVehicle}
            routeDetails={currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject}
            driverAuthData={currentDriverAuthData}
            shiftObject={currentDriverAuthData?.shiftObject}
            scheduleType={currentStage === 'shuttlebus-looped' ? 'looped' : 'fixed'}
            onRouteUpdate={(updatedRouteDetails) => {
              
              // Check if the route is complete based on the update
              const isRouteComplete = updatedRouteDetails?.isComplete || 
                                    updatedRouteDetails?.updateSource === 'routeCompletion' ||
                                    (updatedRouteDetails?.itinerary && updatedRouteDetails.itinerary.length === 0);
              
              if (isRouteComplete) {
                // Clear itinerary to indicate completion and trigger completeShift in determineDispatchStage
                updatedRouteDetails = {
                  ...updatedRouteDetails,
                  itinerary: [], // Empty itinerary triggers completeShift case
                  isComplete: true,
                  completedAt: new Date().toISOString()
                };
                
                // Force immediate navigation to completeShift
                // This is more direct than waiting for stage determination
                setTimeout(() => {
                  // Use a timeout to ensure state updates are processed first
                  const currentStage = determineDispatchStage();
                  if (currentStage !== 'completeShift') {
                    // If stage determination doesn't route to completeShift, force it
                    // We can't directly call setCurrentStage here, but we can trigger a re-render
                    // by updating the auth data which will cause determineDispatchStage to re-run
                  }
                }, 100);
              }
              
              // Update driver auth data with the updated route
              const updatedAuthData = {
                ...currentDriverAuthData,
                routeObject: updatedRouteDetails
              };
              setCurrentDriverAuthData(updatedAuthData);
              
              // Also notify parent component
              if (onDispatchDataUpdate) {
                onDispatchDataUpdate({
                  driverAuthData: updatedAuthData,
                  routeDetails: updatedRouteDetails
                });
              }
            }}
          />
        );

      case 'itinerary':
        return (
          <ItineraryDispatch
            socket={socket}
            fleetSettings={fleetSettings}
            userSettings={effectiveUserSettings}
            driverData={currentDriverAuthData}
            navigateTo={navigateTo}
            routeDetails={currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject}
            selectedVehicle={selectedVehicle}
            onBack={() => navigateTo('login')}
            isDispatchMode={true}
          />
        );

      case 'demandresponse':
        return (
          <DemandResponse
            socket={socket}
            fleetSettings={fleetSettings}
            userSettings={effectiveUserSettings}
            driverData={currentDriverAuthData}
            navigateTo={navigateTo}
            routeDetails={currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject}
            selectedVehicle={selectedVehicle}
            onBack={() => navigateTo('login')}
            isDispatchMode={true}
          />
        );

      case 'nightowl':
        return (
          <Nightowl 
            navigateTo={navigateTo}
            userSettings={effectiveUserSettings}
            fleetSettings={fleetSettings}
            socket={socket}
            selectedVehicle={selectedVehicle}
            routeDetails={currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject}
            driverAuthData={currentDriverAuthData}
            shiftObject={currentDriverAuthData?.shiftObject}
            onBack={() => navigateTo('login')}
          />
        );

      case 'completeShift':
        return (
          <CompleteShift 
            navigateTo={navigateTo}
            userSettings={effectiveUserSettings}
            fleetSettings={fleetSettings}
            socket={socket}
            driverAuthData={currentDriverAuthData}
            routeDetails={currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject}
            vehicleDetails={selectedVehicle}
            selectedVehicle={selectedVehicle}
            googleMapsLoaded={googleMapsLoaded}
            inspectionData={currentDriverAuthData?.inspectionObject || currentDriverAuthData?.inspectionData}
            onShiftComplete={(completionData) => {
              // Navigate based on user's default screen preference
              const isDashboardUser = currentDriverAuthData?.defaultScreen === 'dashboard';
              navigateTo(isDashboardUser ? 'dashboard' : 'login');
            }}
          />
        );

      case 'live':
      default:
        return (
          <LiveDispatch 
            navigateTo={navigateTo}
            userSettings={effectiveUserSettings}
            fleetSettings={fleetSettings}
            socket={socket}
            selectedVehicle={selectedVehicle}
            routeDetails={currentDriverAuthData?.routeObject || currentDriverAuthData?.dispatchData?.routeObject}
            driverAuthData={currentDriverAuthData}
            shiftObject={currentDriverAuthData?.shiftObject}
            onBack={() => navigateTo('login')}
            googleMapsLoaded={!!googleMapsLoaded}
          />
        );
    }
  };

  return renderDispatchStage();
}

export default Dispatch;
